import * as React from "react";
import Image from "next/image";
import Icon from "../../Icon";
import { motion } from "framer-motion";
import AddToCalendar from "@culturehq/add-to-calendar";
import "@culturehq/add-to-calendar/dist/styles.css";
import { IParts } from "@src/components/Parts";
import DialogModal from "../../Dialog";
import LiveSchedules from "../../LiveSchedules";
import { GoogleMap, useLoadScript, MarkerF } from "@react-google-maps/api";
import Button from "@src/components/Button";
import formatDateWithOrdinal, {
  formatEventTime,
} from "@src/utils/dateFormatter";
import { shimmer, toBase64 } from "@src/utils/shimmer";
import flowerBackground from "../../../../public/images/bw-flower.jpeg";
import { useEventParts } from "@src/hooks/useEventParts";

const BG_COLOR = "bg-[#EAE4CC]";
const CARD_BG = "bg-white";
const CARD_BORDER = "border-[#fff]";
const BUTTON_BG = "bg-white";
const BUTTON_TEXT = "text-[#725A3F]";
const TITLE_TEXT = "text-[#725A3F]";
const TEXT_PRIMARY = "text-[#725A3F]";
const TEXT_ACCENT = "text-[#EAE4CC]";

const Parts: React.FC<IParts> = ({
  eventId,
  parts,
  eventPartsData,
  liveSchedules,
  eventTimezone,
  albumDetailsData,
  isInvitedToEvent,
}) => {
  const [openMap, setOpenMap] = React.useState<boolean>(false);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY || "",
    libraries: ["places"],
  });

  const onLoadMarker = (marker: any) => {
    // console.log("Marker", marker?.position?.lat);
  };

  const handleViewDirections = ({
    latitude,
    longitude,
  }: {
    latitude: number;
    longitude: number;
  }) => {
    const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${latitude},${longitude}&travelmode=driving`;
    window.open(directionsUrl, "_blank");
  };

  const handleFindHotelsNearby = ({
    latitude,
    longitude,
  }: {
    latitude: number;
    longitude: number;
  }) => {
    const hotelsUrl = `https://www.google.com/maps/search/hotels/@${latitude},${longitude},15z`;
    window.open(hotelsUrl, "_blank");
  };

  const { parts: derivedParts } = useEventParts({
    eventPartsData,
    liveSchedules,
    eventTimezone,
    isInvitedToEvent,
  });
  const visibleParts = parts ?? derivedParts;

  const addOneDay = (dateTime: string) => {
    var date = new Date(dateTime);
    date.setHours(date.getHours() + 24);
    var isoDate = date.toISOString();
    return isoDate;
  };

  return visibleParts.length > 0 ? (
    <div className={`relative overflow-hidden ${BG_COLOR}`}>
      <div className="absolute inset-0" />

      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={{
          hidden: { opacity: 0 },
          visible: {
            opacity: 1,
            transition: { delayChildren: 0.3, staggerChildren: 0.15 },
          },
        }}
        className="relative z-10 py-6 lg:py-12 px-4"
      >
        <motion.div
          variants={{
            hidden: { opacity: 0, y: -30 },
            visible: {
              opacity: 1,
              y: 0,
              transition: { duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] },
            },
          }}
          className="text-center mb-8"
        >
          <h3
            className={`font-bauerBodoni text-2xl md:text-4xl font-light text-[#454545] uppercase tracking-wider ${TITLE_TEXT}`}
          >
            The Event Schedule
          </h3>
        </motion.div>

        {/* Event Cards */}
        <motion.div
          variants={{
            hidden: { opacity: 0 },
            visible: {
              opacity: 1,
              transition: { delayChildren: 0.3, staggerChildren: 0.15 },
            },
          }}
          className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:items-stretch"
        >
          {visibleParts.map((item, idx) => {
            const eventImage = albumDetailsData?.getMedia?.[idx];
            return (
              <motion.div
                key={`event-${idx}`}
                className="h-full flex flex-col"
                variants={{
                  hidden: { opacity: 0, y: 50, scale: 0.95 },
                  visible: {
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    transition: {
                      duration: 0.6,
                      ease: [0.25, 0.46, 0.45, 0.94],
                    },
                  },
                }}
                whileHover={{ scale: 1.02 }}
                transition={{ duration: 0.3 }}
              >
                <div
                  className={`border ${CARD_BORDER} p-2 w-full h-full transition-all duration-500 hover:scale-[1.02]`}
                >
                  <div
                    className={`${CARD_BG} border border-[#b18958]/20 shadow-xl lg:hover:shadow-2xl transition-all duration-500 w-full h-full overflow-hidden flex flex-col`}
                  >
                    {/* Image (always top, full bleed) */}
                    {eventImage && (
                      <div className="w-full h-56 overflow-hidden relative flex-shrink-0">
                        <Image
                          src={
                            eventImage?.meta?.s3_url ||
                            eventImage?.url ||
                            flowerBackground
                          }
                          alt={`Event ${item.name}`}
                          layout="fill"
                          objectFit="cover"
                          placeholder="blur"
                          blurDataURL={`data:image/svg+xml;base64,${toBase64(
                            shimmer(300, 300)
                          )}`}
                          sizes="(max-width: 768px) 100vw, 33vw"
                          onError={(event) => {
                            const target =
                              event.currentTarget as HTMLImageElement;
                            if (!target.src.includes("bw-flower.jpeg")) {
                              target.src = flowerBackground.src;
                            }
                          }}
                        />
                      </div>
                    )}

                    {/* Event Details (always below image) */}
                    <div className="flex flex-col p-6 flex-1 overflow-y-auto [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
                      {/* Event Name */}
                      <h4 className="text-xl lg:text-2xl font-bauerBodoni text-[#454545] tracking-wide uppercase mb-4">
                        {item.name}
                      </h4>

                      {/* Time, Date & Address */}
                      <div className="space-y-3">
                        {/* Time and Date */}
                        <div className="flex items-center text-[#454545] font-openSans text-sm">
                          <span className="font-medium">
                            {formatEventTime(
                              item.time,
                              item.eventPartTimezone ?? eventTimezone
                            ).trim()}
                          </span>
                          <span className="mx-0.5">,</span>
                          <span className="font-medium">
                            {formatDateWithOrdinal(
                              item.time,
                              item.eventPartTimezone ?? eventTimezone
                            ).trim()}
                          </span>
                        </div>

                        {/* Address */}
                        {item.showAddress && (
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-[#454545] font-openSans text-sm">
                              {item?.address}
                            </span>
                          </div>
                        )}
                      </div>

                      {/* Action Buttons — pinned to bottom */}
                      <div className="flex flex-row flex-wrap gap-2 mt-auto pt-4">
                        {item.showAddress && (
                          <div className="inline-flex items-center justify-center w-[130px] bg-white text-[#b18958] border border-[#b18958] rounded-full font-medium hover:bg-[#b18958] transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer group">
                            <AddToCalendar
                              event={{
                                name: item.name,
                                details: null,
                                location: item?.address,
                                startsAt: item?.time,
                                endsAt: addOneDay(item?.time),
                              }}
                            >
                              <span
                                className={`flex items-center justify-center text-[#b18958] font-bold group-hover:text-[#EAE4CC] transition-all duration-300 text-sm font-bauerBodoni uppercase tracking-wide`}
                              >
                                Save Date
                              </span>
                            </AddToCalendar>
                          </div>
                        )}
                        {item.showAddress &&
                          item.hasLiveStream &&
                          liveSchedules && (
                            <DialogModal
                              fillScreen={true}
                              trigger={
                                <button
                                  className={`px-4 py-2 w-[130px] bg-white text-[#b18958] border border-[#b18958] rounded-full font-bold text-sm hover:bg-[#b18958] hover:text-[#EAE4CC] transition-all duration-300 shadow-lg hover:shadow-xl cursor-pointer font-bauerBodoni uppercase tracking-wide`}
                                >
                                  Watch Live
                                </button>
                              }
                            >
                              <LiveSchedules schedules={liveSchedules} />
                            </DialogModal>
                          )}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Bottom decorative section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.6 }}
          className="text-center mt-16 px-4"
        >
          {/* <div className="max-w-2xl mx-auto">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-[#EAE4CC]/20 via-[#EAE4CC]/40 to-[#EAE4CC]/20 rounded-2xl blur-xl"></div>
              <div className="relative bg-white rounded-2xl py-8 px-8 border border-[#fff]">
                <div className="text-center">
                  <span className="text-[#454545] text-xl lg:text-2xl font-light italic tracking-wide">
                    Love is in the details!
                  </span>
                  <div className="mt-4 flex justify-center">
                    <div className="w-16 h-[2px] bg-[#b18958] rounded-full"></div>
                  </div>
                </div>
              </div>
            </div>
          </div> */}
        </motion.div>
      </motion.div>
    </div>
  ) : null;
};

export default Parts;
